import React, { useState } from 'react';
import { Settings as SettingsIcon, Shield, User, Mail, Key, Save, CheckCircle2, AlertCircle, Eye, EyeOff, Trash2, Edit2, Bell, Palette } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { authHelper } from '../utils/authHelper';
import { NotificationsSettings } from './settings/NotificationsSettings';
import { AppearanceSettings } from './settings/AppearanceSettings';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function SettingsPage({ user, onUpdateUser, onLogout }) {
  const { t, language } = useLanguage();
  const [activeSection, setActiveSection] = useState('profile');
  
  // Profile form
  const [username, setUsername] = useState(user?.username || '');
  const [email, setEmail] = useState(user?.email || '');
  const [pictureUrl, setPictureUrl] = useState(user?.picture || '');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  
  // Password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const updatePayload = {};
      
      if (username !== user?.username) updatePayload.username = username;
      if (email !== user?.email) updatePayload.email = email;
      if (pictureUrl !== user?.picture) updatePayload.picture = pictureUrl;
      
      const response = await fetch(`${API_URL}/api/auth/update-profile`, {
        method: 'POST',
        headers: authHelper.getAuthHeaders(),
        body: JSON.stringify(updatePayload)
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(language === 'es' ? 'Perfil actualizado correctamente' : 'Profile updated successfully');
        onUpdateUser({ ...user, username, email, picture: pictureUrl });
      } else {
        setError(data.detail || data.error || (language === 'es' ? 'Error al actualizar perfil' : 'Error updating profile'));
      }
    } catch (err) {
      setError(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError(language === 'es' ? 'Por favor selecciona una imagen válida' : 'Please select a valid image');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError(language === 'es' ? 'La imagen es muy grande. Máximo 5MB.' : 'Image too large. Max 5MB.');
      return;
    }

    setUploadingPhoto(true);
    setError('');

    // Convert to base64 data URL
    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result;
      if (typeof dataUrl === 'string') {
        setPictureUrl(dataUrl);
        setUploadingPhoto(false);
      }
    };
    reader.onerror = () => {
      setError(language === 'es' ? 'Error al leer la imagen' : 'Error reading image');
      setUploadingPhoto(false);
    };
    reader.readAsDataURL(file);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError(language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match');
      setLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setError(language === 'es' ? 'La contraseña debe tener al menos 6 caracteres' : 'Password must be at least 6 characters');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/update-profile`, {
        method: 'POST',
        headers: authHelper.getAuthHeaders(),
        body: JSON.stringify({ 
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(language === 'es' ? 'Contraseña actualizada correctamente' : 'Password updated successfully');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(data.detail || data.error || (language === 'es' ? 'Error al cambiar contraseña' : 'Error changing password'));
      }
    } catch (err) {
      setError(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm(t.settings.deleteAccountConfirm)) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/auth/delete-account`, {
        method: 'DELETE',
        headers: authHelper.getAuthHeaders()
      });

      if (response.ok) {
        onLogout();
      } else {
        const data = await response.json();
        setError(data.detail || data.error || 'Error deleting account');
      }
    } catch (err) {
      setError(language === 'es' ? 'Error de conexión' : 'Connection error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-2 sm:p-4 space-y-4 sm:space-y-6" data-testid="settings-page">
      <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
          <SettingsIcon size={20} />
        </div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-800">{t.settings.title}</h1>
      </div>

      {/* Messages */}
      {error && (
        <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2 border border-red-100">
          <AlertCircle size={16} />
          {error}
        </div>
      )}
      
      {success && (
        <div className="p-3 bg-green-50 text-green-600 text-sm rounded-lg flex items-center gap-2 border border-green-100">
          <CheckCircle2 size={16} />
          {success}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Sidebar */}
        <div className="space-y-2">
          <button 
            onClick={() => { setActiveSection('profile'); setError(''); setSuccess(''); }}
            className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl font-medium transition-all text-sm ${
              activeSection === 'profile' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <User size={18} />
            {language === 'es' ? 'Perfil' : 'Profile'}
          </button>
          <button 
            onClick={() => { setActiveSection('password'); setError(''); setSuccess(''); }}
            className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl font-medium transition-all text-sm ${
              activeSection === 'password' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Key size={18} />
            {language === 'es' ? 'Contraseña' : 'Password'}
          </button>
          <button 
            onClick={() => { setActiveSection('security'); setError(''); setSuccess(''); }}
            className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl font-medium transition-all text-sm ${
              activeSection === 'security' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Shield size={18} />
            {language === 'es' ? 'Seguridad' : 'Security'}
          </button>
          <button 
            onClick={() => { setActiveSection('notifications'); setError(''); setSuccess(''); }}
            data-testid="settings-notifications-tab"
            className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl font-medium transition-all text-sm ${
              activeSection === 'notifications' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Bell size={18} />
            {language === 'es' ? 'Notificaciones' : 'Notifications'}
          </button>
          <button 
            onClick={() => { setActiveSection('appearance'); setError(''); setSuccess(''); }}
            data-testid="settings-appearance-tab"
            className={`w-full flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 rounded-xl font-medium transition-all text-sm ${
              activeSection === 'appearance' ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Palette size={18} />
            {language === 'es' ? 'Apariencia' : 'Appearance'}
          </button>
        </div>

        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          
          {/* Profile Section */}
          {activeSection === 'profile' && (
            <form onSubmit={handleUpdateProfile} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-50">
                <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
                  <User size={18} className="text-blue-500" />
                  {language === 'es' ? 'Información del Perfil' : 'Profile Information'}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {language === 'es' ? 'Actualiza tu nombre de usuario y email' : 'Update your username and email'}
                </p>
              </div>
              
              <div className="p-4 sm:p-6 space-y-4">
                {/* User ID (read-only) */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {language === 'es' ? 'ID de Usuario' : 'User ID'}
                  </label>
                  <div className="flex items-center gap-2 p-2.5 sm:p-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-400 font-mono text-sm">
                    {user?.user_id || 'N/A'}
                  </div>
                </div>

                {/* Profile Picture */}
                <div className="space-y-3">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {language === 'es' ? 'Foto de Perfil' : 'Profile Picture'}
                  </label>
                  
                  {/* Current Picture Preview & Upload Section */}
                  <div className="flex flex-col sm:flex-row items-center gap-4 p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
                    {/* Large Preview */}
                    {pictureUrl ? (
                      <img 
                        src={pictureUrl}
                        alt="Profile"
                        className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover border-4 border-blue-500 shadow-lg"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white font-bold text-4xl border-4 border-blue-500 shadow-lg">
                        {(username || email || 'U')[0].toUpperCase()}
                      </div>
                    )}
                    
                    <div className="flex-1 w-full space-y-3">
                      {/* Big Upload Button */}
                      <div className="w-full">
                        <input
                          type="file"
                          id="photo-upload"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                        <label
                          htmlFor="photo-upload"
                          className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl cursor-pointer transition-all transform active:scale-95 shadow-lg hover:shadow-xl text-base"
                        >
                          {uploadingPhoto ? (
                            <>
                              <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                              {language === 'es' ? 'Subiendo Foto...' : 'Uploading Photo...'}
                            </>
                          ) : (
                            <>
                              <User size={20} />
                              {language === 'es' ? '📸 Subir Foto desde Dispositivo' : '📸 Upload Photo from Device'}
                            </>
                          )}
                        </label>
                        <p className="text-xs text-gray-600 mt-2 text-center font-medium">
                          {language === 'es' 
                            ? '↑ Toca aquí para seleccionar una imagen (máx. 5MB)' 
                            : '↑ Tap here to select an image (max 5MB)'}
                        </p>
                      </div>
                      
                      {/* URL Input (alternative) */}
                      <div className="relative">
                        <input
                          type="url"
                          value={pictureUrl}
                          onChange={(e) => setPictureUrl(e.target.value)}
                          className="w-full px-4 py-3 bg-white border-2 border-blue-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all text-sm"
                          placeholder={language === 'es' ? 'O pega una URL de imagen (https://...)' : 'Or paste an image URL (https://...)'}
                        />
                        <p className="text-xs text-gray-500 mt-1.5 text-center">
                          {language === 'es' 
                            ? 'También puedes usar una URL de imagen externa' 
                            : 'You can also use an external image URL'}
                        </p>
                      </div>
                      
                      {/* Remove Photo Button */}
                      {pictureUrl && (
                        <button
                          type="button"
                          onClick={() => setPictureUrl('')}
                          className="w-full text-sm text-red-600 hover:text-red-700 font-bold py-2 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          {language === 'es' ? '🗑️ Eliminar Foto' : '🗑️ Remove Photo'}
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* Username */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {language === 'es' ? 'Nombre de Usuario' : 'Username'}
                  </label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                      placeholder={language === 'es' ? 'Tu nombre de usuario' : 'Your username'}
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {language === 'es' ? 'Correo Electrónico' : 'Email Address'}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                      placeholder={language === 'es' ? 'tu@email.com' : 'you@email.com'}
                    />
                  </div>
                </div>

                {/* Role (read-only) */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {language === 'es' ? 'Rol de Usuario' : 'User Role'}
                  </label>
                  <div className="flex items-center gap-2 p-2.5 sm:p-3 bg-gray-50 border border-gray-200 rounded-xl text-gray-500">
                    <Shield size={16} />
                    <span className="font-medium text-sm">{user?.isAdmin ? (language === 'es' ? 'Administrador' : 'Administrator') : (language === 'es' ? 'Usuario Estándar' : 'Standard User')}</span>
                  </div>
                </div>
              </div>

              <div className="p-4 sm:p-6 bg-gray-50 flex justify-end">
                <button
                  type="submit"
                  disabled={loading || (username === user?.username && email === user?.email && pictureUrl === user?.picture)}
                  className="flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 active:scale-95 text-sm"
                >
                  {loading ? (language === 'es' ? 'Guardando...' : 'Saving...') : (
                    <>
                      <Save size={18} />
                      {language === 'es' ? 'Guardar Cambios' : 'Save Changes'}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Password Section */}
          {activeSection === 'password' && (
            <form onSubmit={handleChangePassword} className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-50">
                <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Key size={18} className="text-blue-500" />
                  {language === 'es' ? 'Cambiar Contraseña' : 'Change Password'}
                </h2>
                <p className="text-xs text-gray-500 mt-1">
                  {language === 'es' ? 'Asegúrate de usar una contraseña segura' : 'Make sure to use a secure password'}
                </p>
              </div>
              
              <div className="p-4 sm:p-6 space-y-4">
                {/* Current Password */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {language === 'es' ? 'Contraseña Actual' : 'Current Password'}
                  </label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type={showPasswords ? "text" : "password"}
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      className="w-full pl-10 pr-12 py-2.5 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                      placeholder="••••••••"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPasswords(!showPasswords)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      {showPasswords ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {language === 'es' ? 'Nueva Contraseña' : 'New Password'}
                  </label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type={showPasswords ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      className="w-full pl-10 pr-4 py-2.5 sm:py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                      placeholder="••••••••"
                      required
                      minLength={6}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {language === 'es' ? 'Mínimo 6 caracteres' : 'Minimum 6 characters'}
                  </p>
                </div>

                {/* Confirm Password */}
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {language === 'es' ? 'Confirmar Contraseña' : 'Confirm Password'}
                  </label>
                  <div className="relative">
                    <Key className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
                    <input
                      type={showPasswords ? "text" : "password"}
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`w-full pl-10 pr-4 py-2.5 sm:py-3 bg-gray-50 border rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm ${
                        confirmPassword && newPassword !== confirmPassword ? 'border-red-300' : 'border-gray-200'
                      }`}
                      placeholder="••••••••"
                      required
                    />
                  </div>
                  {confirmPassword && newPassword !== confirmPassword && (
                    <p className="text-xs text-red-500 mt-1">
                      {language === 'es' ? 'Las contraseñas no coinciden' : 'Passwords do not match'}
                    </p>
                  )}
                </div>
              </div>

              <div className="p-4 sm:p-6 bg-gray-50 flex justify-end">
                <button
                  type="submit"
                  disabled={loading || !currentPassword || !newPassword || newPassword !== confirmPassword}
                  className="flex items-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-blue-600 text-white rounded-xl font-bold hover:bg-blue-700 transition-all shadow-lg shadow-blue-200 disabled:opacity-50 active:scale-95 text-sm"
                >
                  {loading ? (language === 'es' ? 'Actualizando...' : 'Updating...') : (
                    <>
                      <Key size={18} />
                      {language === 'es' ? 'Cambiar Contraseña' : 'Change Password'}
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Security Section */}
          {activeSection === 'security' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6 space-y-6">
              <div>
                <h2 className="text-base sm:text-lg font-bold text-gray-800 flex items-center gap-2 mb-2">
                  <Shield size={18} className="text-blue-500" />
                  {language === 'es' ? 'Seguridad de la Cuenta' : 'Account Security'}
                </h2>
              </div>
              
              {/* Recovery Code */}
              {user?.recoveryCode && (
                <div className="p-3 sm:p-4 bg-blue-50 rounded-xl border border-blue-100">
                  <h3 className="text-sm font-bold text-blue-800 mb-1">
                    {language === 'es' ? 'Tu Código de Recuperación' : 'Your Recovery Code'}
                  </h3>
                  <p className="text-xs text-blue-600 mb-3">
                    {language === 'es' 
                      ? 'Este código es necesario para restablecer tu contraseña si la olvidas. Guárdalo en un lugar seguro.'
                      : 'This code is required to reset your password if you forget it. Keep it in a safe place.'}
                  </p>
                  <div className="p-2.5 sm:p-3 bg-white border border-blue-200 rounded-lg font-mono text-center text-blue-900 font-black text-lg sm:text-xl tracking-widest select-all">
                    {user.recoveryCode}
                  </div>
                </div>
              )}

              {/* Google Account Info */}
              {user?.picture && (
                <div className="p-3 sm:p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <h3 className="text-sm font-bold text-gray-800 mb-2">
                    {language === 'es' ? 'Cuenta de Google Vinculada' : 'Linked Google Account'}
                  </h3>
                  <div className="flex items-center gap-3">
                    <img src={user.picture} alt="Profile" className="w-10 h-10 rounded-full" />
                    <div>
                      <p className="font-medium text-gray-900 text-sm">{user.name}</p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Delete Account */}
              <div className="pt-4 border-t border-gray-100">
                <h3 className="text-sm font-bold text-red-600 mb-1">
                  {language === 'es' ? 'Eliminar Cuenta' : 'Delete Account'}
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  {language === 'es' 
                    ? 'Esta acción es permanente y borrará toda tu información (Bitácora, Plan, Práctica, etc.).'
                    : 'This action is permanent and will delete all your information (Logbook, Plan, Practice, etc.).'}
                </p>
                <button
                  onClick={handleDeleteAccount}
                  disabled={loading}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-50 text-red-600 rounded-lg font-bold hover:bg-red-100 transition-all border border-red-100 active:scale-95 disabled:opacity-50 text-sm"
                  data-testid="delete-account-btn"
                >
                  <Trash2 size={16} />
                  {language === 'es' ? 'Eliminar Cuenta' : 'Delete Account'}
                </button>
              </div>
            </div>
          )}

          {/* Notifications Section */}
          {activeSection === 'notifications' && (
            <NotificationsSettings setError={setError} setSuccess={setSuccess} />
          )}

          {/* Appearance Section */}
          {activeSection === 'appearance' && <AppearanceSettings />}
        </div>
      </div>
    </div>
  );
}
